import { AsyncPipe } from '@angular/common';
import { Component, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { WorkplaceDetailResponsiblePersonsStore } from './workplace-detail-responsible-persons.store';
import { FilterInput } from '@it2go/data-grid';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FormBuilder, Validators } from '@angular/forms';
import { WorkplaceResponsiblePersonGridItemObject } from '@it2go/types';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { isInvalid } from '../../../../shared/helpers/validate';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { userChipColumn } from '../../../../shared/components/kit/table/table.fields';
import { CardModule } from '@openng/optimus-ui/card';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { TranslatePipe } from '@ngx-translate/core';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import {
    TextAreaInputComponent,
} from '../../../../../components/kit/input/text-area-input/text-area-input.component';
import { SelectUserComponent } from '../../common/select-user/select-user.component';

@Component({
    selector: 'app-workplace-detail-responsible-persons',
    templateUrl: './workplace-detail-responsible-persons.component.html',
    providers: [WorkplaceDetailResponsiblePersonsStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonsComponent,
        AsyncPipe,
        CardModule,
        DateInputComponent,
        DialogModule,
        PrimeTemplate,
        SelectUserComponent,
        TableComponent,
        TextAreaInputComponent,
        TranslatePipe,
    ],
})
export class WorkplaceDetailResponsiblePersonsComponent extends SubscriptionComponent {

    protected readonly store = inject(WorkplaceDetailResponsiblePersonsStore);
    private readonly fb = inject(FormBuilder);
    private readonly confirmBuilder = inject(ConfirmBuilder);

    workplaceId: number | null = null;
    personsAdditionalFilter: FilterInput[][] = [];

    form = this.fb.group({
        id: this.fb.control<number | null>(null),
        validFrom: this.fb.control<Date>(new Date(), Validators.required),
        userId: this.fb.control<string | null>(null, Validators.required),
        note: this.fb.control<string | null>(null),
    })

    modalVisible = false;

    columns: TableColumn[] = [
        userChipColumn,
        {
            name: 'validFrom',
            type: TableColumnType.Date,
            filterable: undefined,
            sortable: true,
        },
        {
            name: 'validTo',
            type: TableColumnType.Date,
            filterable: undefined,
            sortable: true,
        },
        {
            name: 'note',
        },
    ];

    @ViewChild('personsTable') personsTable!: TableComponent;

    constructor() {
        super();

        this.store.doOnActions(
            () => this.personsTable.reload(),
            'createWorkplaceResponsiblePerson',
            'updateWorkplaceResponsiblePerson',
            'deleteWorkplaceResponsiblePerson',
        )

        this.store.registerSuccessMessage('createWorkplaceResponsiblePerson', 'common.created');
        this.store.registerSuccessMessage('updateWorkplaceResponsiblePerson', 'common.updated');
        this.store.registerSuccessMessage('deleteWorkplaceResponsiblePerson', 'common.deleted');

        this.subs.push(
            this.store.routeParam.workplaceId.subscribe((workplaceId) => {
                if (workplaceId) {
                    this.workplaceId = Number(workplaceId);
                    this.personsAdditionalFilter = [[{ column: 'workplaceId', operator: 'EQ', value: [workplaceId] }]];
                }
            }),
        )
    }

    openCreateResponsiblePerson() {
        this.modalVisible = true;
    }

    openEditResponsiblePerson(person: WorkplaceResponsiblePersonGridItemObject) {
        this.form.patchValue({
            id: person.id,
            validFrom: person.validFrom ? new Date(person.validFrom) : new Date(),
            userId: person.userId,
            note: person.note,
        });
        this.modalVisible = true;
    }

    closeModal() {
        this.modalVisible = false;
        this.form.reset();
    }

    submit() {
        if (isInvalid(this.form)) return;

        const data = {
            validFrom: this.form.controls.validFrom.value!.toISOString(),
            userId: this.form.controls.userId.value!,
            note: this.form.controls.note.value || null,
        }

        if (this.form.controls.id.value) {
            this.store.api.updateWorkplaceResponsiblePerson({
                id: this.form.controls.id.value,
                ...data,
            });
        } else {
            this.store.api.createWorkplaceResponsiblePerson({
                workplaceId: this.workplaceId!,
                ...data,
            });
        }
        this.closeModal();
    }

    delete(item: any) {
        this.confirmBuilder.confirm(
            'workplace.responsiblePersons.deleteResponsiblePersonConfirmation.header',
            'workplace.responsiblePersons.deleteResponsiblePersonConfirmation.label',
            () => {
                this.store.api.deleteWorkplaceResponsiblePerson({ id: item.item.id });
            },
            { name: 'workplaceResponsiblePersonDeleteConfirm' + item.item.id },
            'pi-trash',
            item.event.target,
        );
    }
}
